import React, { useState, useEffect, useRef } from "react"
import { useNavigate, useLocation } from "react-router-dom"
import { CheckCircle, ArrowLeft, AlertTriangle, Scissors, Sparkles } from 'lucide-react'
import { useTheme } from "../contexts/ThemeContext"

const API_URL = process.env.REACT_APP_API_URL;

const FinalizarPedido = () => {
  const navigate = useNavigate()
  const location = useLocation()

  // ─── TEMA DINÁMICO ───────────────────────────────────────────────────
  const { theme } = useTheme()
  const bgPrimary = { backgroundColor: theme.primary }
  const textPrimary = { color: theme.primary }
  const textPrimaryDark = { color: theme.primaryDark }
  // ─────────────────────────────────────────────────────────────────────

  // Estados para controlar la UI
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [pedidoData, setPedidoData] = useState(null)

  // Ref para prevenir ejecuciones múltiples
  const ejecutado = useRef(false)

  // Extraer pedidoId y otros datos de state (incluyendo tipo si existe)
  const { pedidoId, npedidoCliente, proveedorId, tipo, necesitaAprobacion } = location.state || {}

  // Función para obtener el texto legible del tipo
  const getTipoTexto = (tipo) => {
    if (tipo === 'cirugia_plastica') return 'Cirugía Plástica'
    if (tipo === 'medicina_estetica') return 'Medicina Estética'
    return null
  }

  // Componente para el badge de tipo
  const TipoBadge = ({ tipo }) => {
    if (!tipo) return null

    if (tipo === 'cirugia_plastica') {
      return (
        <div
          className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium"
          style={{ backgroundColor: theme.primaryLight, color: theme.primaryDark }}
        >
          <Scissors size={16} className="mr-2" />
          Cirugía Plástica
        </div>
      )
    }

    if (tipo === 'medicina_estetica') {
      return (
        <div className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-purple-100 text-purple-800">
          <Sparkles size={16} className="mr-2" />
          Medicina Estética
        </div>
      )
    }

    return null
  }

  useEffect(() => {
    // PREVENIR EJECUCIÓN MÚLTIPLE
    if (ejecutado.current) {
      console.log("FinalizarPedido: Ya ejecutado, saltando")
      return
    }

    // Verificar que tenemos los datos necesarios
    if (!pedidoId || !proveedorId) {
      setError("Datos del pedido incompletos. Por favor, vuelva a intentarlo.")
      setLoading(false)
      return
    }

    // MARCAR COMO EJECUTADO INMEDIATAMENTE
    ejecutado.current = true

    const finalizarPedido = async () => {
      try {
        const token = localStorage.getItem("token")

        console.log(`FinalizarPedido: Iniciando finalización de pedido ${pedidoId}`)
        console.log(`FinalizarPedido: Tipo de pedido: ${tipo || 'No especificado'}`)

        // Preparar el body con los datos necesarios
        const bodyData = {
          npedidoc: npedidoCliente,
          proveedor_id: proveedorId,
        }

        // Incluir tipo solo si está presente
        if (tipo) {
          bodyData.tipo = tipo
          console.log(`FinalizarPedido: Incluyendo tipo en request: ${tipo}`)
        }

        // Llamar a la API para finalizar el pedido
        const response = await fetch(`${API_URL}/api/pedidos/${pedidoId}/finalizar-completo`, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(bodyData),
        })

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}))
          console.error("Error del servidor:", errorData)
          throw new Error(errorData.error || "Error al finalizar el pedido")
        }

        const data = await response.json()
        console.log("FinalizarPedido: Pedido finalizado correctamente")

        setPedidoData(data)
        setLoading(false)
      } catch (error) {
        console.error("Error al finalizar el pedido:", error)
        setError(error.message || "Ocurrió un error al procesar el pedido. Por favor, contacte con soporte.")
        setLoading(false)

        // RESETEAR el flag en caso de error para permitir reintentos manuales si es necesario
        ejecutado.current = false
      }
    }

    finalizarPedido()
  }, []) // ← DEPENDENCIAS VACÍAS: Solo se ejecuta una vez al montar

  const handleVolver = () => {
    navigate("/gestion-clientes")
  }

  // ─── Pantalla de carga ────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-lg p-8 max-w-md w-full text-center">
          <div
            className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 mx-auto mb-4"
            style={{ borderColor: theme.primary }}
          ></div>
          <h2 className="text-2xl font-bold text-gray-800 mb-2">Procesando su pedido</h2>
          <p className="text-gray-600 mb-4">
            {necesitaAprobacion
              ? "Estamos enviando su pedido para aprobación de la clínica matriz. Por favor, espere un momento..."
              : "Estamos finalizando su pedido y enviando las notificaciones correspondientes. Por favor, espere un momento..."
            }
          </p>
          <div className="w-full bg-gray-200 rounded-full h-2.5 mb-4">
            <div
              className="h-2.5 rounded-full animate-pulse"
              style={bgPrimary}
            ></div>
          </div>
        </div>
      </div>
    )
  }

  // ─── Pantalla de error ────────────────────────────────────────────────
  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-lg p-8 max-w-md w-full text-center">
          <div className="flex items-center justify-center mb-4">
            <AlertTriangle size={48} className="text-red-500" />
          </div>
          <h2 className="text-2xl font-bold text-gray-800 mb-2">Error</h2>
          <p className="text-gray-600 mb-6">{error}</p>
          <button
            onClick={handleVolver}
            className="text-white font-medium py-2 px-4 rounded-md transition-colors flex items-center justify-center mx-auto"
            style={bgPrimary}
            onMouseEnter={e => e.currentTarget.style.backgroundColor = theme.primaryDark}
            onMouseLeave={e => e.currentTarget.style.backgroundColor = theme.primary}
          >
            <ArrowLeft size={18} className="mr-2" />
            Volver a Gestión de Clientes
          </button>
        </div>
      </div>
    )
  }

  // ─── Pantalla de éxito ────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-gray-50 p-4">
      <div className="max-w-4xl mx-auto bg-white rounded-lg shadow-lg overflow-hidden">

        {/* Cabecera */}
        <div className="p-6 text-white" style={bgPrimary}>
          <div className="flex items-center">
            <CheckCircle size={32} className="mr-3" />
            <h1 className="text-2xl font-bold">
              {necesitaAprobacion
                ? "¡Pedido Enviado para Aprobación!"
                : "¡Pedido Finalizado Correctamente!"
              }
            </h1>
          </div>
          <p className="mt-2 opacity-90">
            {necesitaAprobacion
              ? "El pedido ha sido enviado a la clínica matriz para su revisión y aprobación."
              : "El pedido ha sido procesado y se han enviado las notificaciones correspondientes."
            }
          </p>
        </div>

        {/* Detalles del pedido */}
        <div className="p-6">
          <div className="mb-6">
            <h2 className="text-xl font-bold text-gray-800 mb-4 border-b pb-2">Detalles del Pedido</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <p className="text-gray-600">Número de Pedido:</p>
                <p className="font-medium">{pedidoData?.pedido?.npedido || "S/N"}</p>
              </div>
              <div>
                <p className="text-gray-600">Fecha:</p>
                <p className="font-medium">{pedidoData?.pedido?.fecha || new Date().toLocaleDateString()}</p>
              </div>
              <div>
                <p className="text-gray-600">Cliente:</p>
                <p className="font-medium">{pedidoData?.cliente?.razon_social || "Cliente"}</p>
              </div>
              <div>
                <p className="text-gray-600">Proveedor:</p>
                <p className="font-medium">{pedidoData?.proveedor?.nombre || "Proveedor"}</p>
              </div>
              {npedidoCliente && (
                <div>
                  <p className="text-gray-600">Nº Pedido Cliente:</p>
                  <p className="font-medium">{npedidoCliente}</p>
                </div>
              )}
              {necesitaAprobacion && (
                <div>
                  <p className="text-gray-600">Estado:</p>
                  <p className="font-medium text-yellow-600">Pendiente de aprobación</p>
                </div>
              )}
              {tipo && (
                <div>
                  <p className="text-gray-600 mb-2">Tipo de Pedido:</p>
                  <TipoBadge tipo={tipo} />
                </div>
              )}
            </div>
          </div>

          {/* Tabla de productos */}
          <div className="mb-6">
            <h2 className="text-xl font-bold text-gray-800 mb-4 border-b pb-2">Productos</h2>
            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="bg-gray-100">
                    <th className="border p-2 text-left">Referencia</th>
                    <th className="border p-2 text-left">Producto</th>
                    <th className="border p-2 text-right">Precio</th>
                    <th className="border p-2 text-center">IVA</th>
                    <th className="border p-2 text-right">Cantidad</th>
                    <th className="border p-2 text-right">Subtotal</th>
                  </tr>
                </thead>
                <tbody>
                  {pedidoData?.lineas?.map((linea) => (
                    <tr key={linea.id} className="hover:bg-gray-50">
                      <td className="border p-2">{linea.ref_proveedor || linea.raiz || "-"}</td>
                      <td className="border p-2">{linea.nombre}</td>
                      <td className="border p-2 text-right">
                        {new Intl.NumberFormat("es-ES", {
                          style: "currency",
                          currency: "EUR",
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        }).format(linea.precio)}
                      </td>
                      <td className="border p-2 text-center">{linea.iva || 21}%</td>
                      <td className="border p-2 text-right">{linea.cantidad}</td>
                      <td className="border p-2 text-right">
                        {new Intl.NumberFormat("es-ES", {
                          style: "currency",
                          currency: "EUR",
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        }).format(linea.subtotal)}
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="bg-gray-100 font-bold">
                    <td colSpan={5} className="border p-2 text-right">Total:</td>
                    <td className="border p-2 text-right" style={textPrimaryDark}>
                      {new Intl.NumberFormat("es-ES", {
                        style: "currency",
                        currency: "EUR",
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      }).format(pedidoData?.total || 0)}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>

          {/* Comentarios */}
          {pedidoData?.comentarios && pedidoData.comentarios.length > 0 && (
            <div className="mb-6">
              <h2 className="text-xl font-bold text-gray-800 mb-4 border-b pb-2">Comentarios</h2>
              <ul className="list-disc pl-5 space-y-2">
                {pedidoData.comentarios.map((comentario) => (
                  <li key={comentario.id} className="text-gray-700">
                    {comentario.comentario}
                    {(comentario.cantidad > 0 || comentario.precio > 0) && (
                      <span className="text-gray-500 ml-2">
                        {comentario.cantidad > 0 && `Cantidad: ${comentario.cantidad}`}
                        {comentario.cantidad > 0 && comentario.precio > 0 && " - "}
                        {comentario.precio > 0 &&
                          `Precio: ${new Intl.NumberFormat("es-ES", {
                            style: "currency",
                            currency: "EUR",
                          }).format(comentario.precio)}`}
                      </span>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Botón de volver */}
          <div className="flex justify-center mt-8">
            <button
              onClick={handleVolver}
              className="text-white font-medium py-3 px-6 rounded-md transition-colors flex items-center"
              style={bgPrimary}
              onMouseEnter={e => e.currentTarget.style.backgroundColor = theme.primaryDark}
              onMouseLeave={e => e.currentTarget.style.backgroundColor = theme.primary}
            >
              <ArrowLeft size={18} className="mr-2" />
              Volver a Gestión de Clientes
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default FinalizarPedido